import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ButtonComponent';
import { LineSeparator } from '@/components/LineSeparator';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { Modal } from '@/feature/alerts/components/Modal';
import { BoostedMultiplier, DynamicMultiplier } from '@/types/api.generated';
import { toLocaleCurrency } from '@/utils/numeric/currency';
import { BottomSheetModal } from '@gorhom/bottom-sheet';

import { getBoostedMultiplierDetails } from '../utils/betslip-utils';

export const DynamicPayoutSheet = memo(
    ({
        payoutSheetRef,
        entryAmount,
        dynamicMultipliers,
        dynamicBoostedMultiplier,
    }: {
        entryAmount: number;
        payoutSheetRef: React.RefObject<BottomSheetModal | null>;
        dynamicMultipliers: DynamicMultiplier[];
        dynamicBoostedMultiplier?: BoostedMultiplier;
    }) => {
        const { t } = useTranslation(['betslip_pickem']);

        const infoText =
            entryAmount <= 0
                ? t('dynamic_payout_info_add_entry')
                : t('dynamic_payout_info_entry_amount', { amount: toLocaleCurrency(entryAmount) });
        return (
            <Modal modalRef={payoutSheetRef} id={'dynamic-payout-sheet'}>
                <Box px={'s16'} mt={'s8'} mb={'s16'}>
                    <Text textAlign={'center'} variant={'titleLarge'} textTransform={'uppercase'}>
                        {t('dynamic_payout_summary')}
                    </Text>
                    <Text variant={'bodySmall'} color={ds.colorToken.text.secondary} mb={'s12'} textAlign={'center'}>
                        {infoText}
                    </Text>

                    {dynamicMultipliers
                        .sort((a, b) => b.winningPicks - a.winningPicks)
                        .map((dynamicMultiplier, index) => {
                            const showSeparator = dynamicMultipliers ? index !== dynamicMultipliers.length - 1 : false;
                            // only the highest multiplier can get boosted, so we only show the boosted multiplier for the first one
                            const { boostedMultiplier, toWin } = getBoostedMultiplierDetails(
                                index === 0,
                                dynamicMultiplier,
                                dynamicBoostedMultiplier
                            );
                            const multiplierText = `${
                                boostedMultiplier && boostedMultiplier > 0
                                    ? boostedMultiplier
                                    : dynamicMultiplier.multiplier
                            }x`;
                            const description = `${dynamicMultiplier.winningPicks} correct`;
                            return (
                                <PayoutLine
                                    key={`win-projection${dynamicMultiplier.multiplier}-${index}`}
                                    description={description}
                                    multiplierText={multiplierText}
                                    toWin={toWin}
                                    showSeparator={showSeparator}
                                />
                            );
                        })}
                    <SizedBox value={16} />
                    <Button label={'Close'} hierarchy={'primary'} onPress={() => payoutSheetRef.current?.close()} />
                </Box>
            </Modal>
        );
    }
);

const PayoutLine = ({
    description,
    multiplierText,
    toWin,
    showSeparator,
}: {
    description: string;
    multiplierText: string;
    toWin: number;
    showSeparator: boolean;
}) => {
    const noAmount = toWin <= 0;
    return (
        <Box>
            <Row minWidth={0} alignItems={'center'} justifyContent={'space-between'}>
                <Row alignItems={'center'}>
                    {description ? (
                        <Text variant={'bodySmall'} paddingVertical={'s16'} mr={'s12'}>
                            {description}
                        </Text>
                    ) : null}
                    <Row
                        paddingVertical={'s4'}
                        borderRadius={'r4'}
                        paddingHorizontal={'s6'}
                        backgroundColor={ds.colorToken.background.neutral.subtle}
                    >
                        {multiplierText ? (
                            <Text variant={'labelMedium'} color={ds.colorToken.text.primary}>
                                {multiplierText}
                            </Text>
                        ) : null}
                    </Row>
                </Row>
                <Text
                    variant={'bodyMedium'}
                    color={noAmount ? ds.colorToken.text.secondary : ds.colorToken.text.primary}
                >
                    {noAmount ? '$--' : toLocaleCurrency(toWin, false)}
                </Text>
            </Row>
            {showSeparator ? <LineSeparator /> : null}
        </Box>
    );
};
